/**
 * D-EZ-108 — WHAT A RACE LEFT BEHIND BETWEEN TWO MEN.
 *
 * The rivalry engine remembers incidents, and every incident here is read off
 * something the dice already produced: a duel that put a man in the wall, a
 * feud or a fume that boiled over, a small crash two cars shared, a pair that
 * spent the afternoon taking the same piece of track off each other, a pass for
 * the lead. Nothing is rolled and nothing is guessed — this file only reads.
 *
 * ⚠ THE TAPE DOES NOT SURVIVE THE WINTER. A settled season is archived without
 * its turn-by-turn events (`lib/seasonArchive.ts`, D-EZ-65), and a rivalry that
 * was boiling in October cannot quietly go cold because the save stripped the
 * evidence. So the incidents read from the tape are DIGESTED here into a few
 * hundred bytes per race (`RaceMemory`) and the archive keeps the digest on the
 * race (`SeasonRaceResult.memory`). `raceMemoryOf` reads, in order: the tape
 * when it is there, the digest when it is not, and nothing when neither is (a
 * season archived before this build) — and it says so (`partial`).
 *
 * The same function computes the digest and reads the tape, so a race read live
 * and the same race read back from a save give the same incidents by
 * construction. `memory.test.ts` checks that across a real archive round-trip.
 *
 * What is NOT in the digest because it survives the archive on its own (and is
 * therefore read where the rivalry fold reads it): photo finishes, late passes
 * for the win (lead changes + the finishing order), head-to-head finishes,
 * feud threads, the post-race interview, seat changes in the winter.
 */

import type { SeasonRaceResult } from '../seasonPlay.ts';
import type { RuleEvent } from '../ruleEvent.ts';

/** What kind of moment two drivers shared. */
export type IncidentKind =
  /** One put the other in the wall: a duel he won and the other was collected, or a feud/fume wreck. */
  | 'wrecked'
  /** A duel won: the loser was left in trouble and got through it. */
  | 'duel'
  /** He went after the other man in a duel and got it wrong. */
  | 'duel-failed'
  /** A feud or a fume boiled over without a wreck — both shuffled back, or trouble handed out. */
  | 'feud-flare'
  /** Both collected in the same small crash (two or three cars). */
  | 'shared-wreck'
  /** The same two cars at each other again and again in one race. */
  | 'battle'
  /** Took the lead off him — a challenge or a run off pit road. */
  | 'lead-pass'
  /** Decided at the line between them. */
  | 'photo-finish'
  /** Took the lead from him late and won. */
  | 'late-pass-win'
  /** A feud between them went on the list (RB/xvi). */
  | 'feud-start'
  /** The interview chart put the feud in front of the cameras. */
  | 'words'
  /** The printed rules ended their feud. */
  | 'hatchet'
  /** He took over the ride the other man had. */
  | 'seat';

/**
 * One incident, as the digest stores it — ids and numbers only, the words are
 * written when it is read (`describeIncident`), so a renamed driver reads right.
 */
export interface Incident {
  k: IncidentKind;
  /** The aggressor — the man who did it — or the first of the pair when nobody did. */
  a: string;
  b: string;
  /** The turn it happened on, when it happened on one. */
  t: number | null;
  /** Detail the words need: 'out' (the victim's day ended), 'lead', 'win', 'self' (backfired), a count for a battle. */
  d?: string;
}

export interface RaceMemory {
  v: 1;
  incidents: Incident[];
}

/** How many challenge/duel exchanges in one race make a "battle". */
export const BATTLE_MIN_EXCHANGES = 3;
/** A crash this size or smaller is personal; a bigger one is just a crash. */
export const SHARED_WRECK_MAX_CARS = 3;

type Ev = RuleEvent & { turn?: number } & Record<string, unknown>;

const pairKey = (a: string, b: string): string => (a < b ? `${a}|${b}` : `${b}|${a}`);

/**
 * Read the incidents out of a race's tape. Pure, and deterministic in the order
 * of the tape. Returns null when there is no tape to read.
 */
export function incidentsFromTape(run: Pick<SeasonRaceResult, 'race'>): Incident[] | null {
  const events = run.race.events as unknown as readonly Ev[];
  if (!events || events.length === 0) return null;
  const out: Incident[] = [];
  const exchanges = new Map<string, { a: string; b: string; n: number; t: number | null }>();
  const exchange = (a: string, b: string, t: number | null) => {
    if (!a || !b || a === b) return;
    const k = pairKey(a, b);
    const e = exchanges.get(k);
    if (e) e.n += 1;
    else exchanges.set(k, { a, b, n: 1, t });
  };

  for (let i = 0; i < events.length; i++) {
    const e = events[i];
    const t = typeof e.turn === 'number' ? e.turn : null;

    if (e.eventType === 'quality-challenge-sequence') {
      const challenges = (e.challenges ?? []) as readonly Ev[];
      for (const c of challenges) {
        const ch = c.challengerId as string | undefined;
        const df = c.defenderId as string | null | undefined;
        if (!ch || !df) continue;
        exchange(ch, df, t);
        if (c.success === true) {
          const wasLeader = (c.positionDeltas ?? []).some((d) => d.driverId === df && d.from === 1);
          if (wasLeader) out.push({ k: 'lead-pass', a: ch, b: df, t });
        }
      }
      continue;
    }

    if (e.eventType === 'pit-lead-challenge') {
      const [a, b] = e.actorIds;
      if (a && b) {
        exchange(a, b, t);
        if (e.momentTag === 'pit.lead-change') out.push({ k: 'lead-pass', a, b, t, d: 'pit' });
      }
      continue;
    }

    if (e.eventType === 'duel') {
      const inst = e.instigatorId as string | undefined;
      const target = e.targetId as string | null | undefined;
      if (!inst || !target || e.declined === true) continue;
      exchange(inst, target, t);
      const pending = e.pendingTrouble as { scope: string; driverId?: string } | null | undefined;
      const inTrouble = pending && pending.scope === 'driver' ? pending.driverId : undefined;
      // The trouble the duel handed out is resolved by the next trouble event
      // for that driver, in the same turn (turn.ts runs it straight after).
      let collected = false;
      let gone = false;
      for (let j = i + 1; j < events.length; j++) {
        const f = events[j];
        if (f.turn !== e.turn) break;
        if (f.eventType !== 'trouble') continue;
        if (!inTrouble || f.actorIds[0] !== inTrouble) continue;
        const ids = (f.collectedIds ?? []) as string[];
        const outs = (f.outIds ?? []) as string[];
        collected = ids.includes(inTrouble);
        gone = outs.includes(inTrouble);
        break;
      }
      const won = e.won === true;
      if (won) {
        if (collected) out.push({ k: 'wrecked', a: inst, b: target, t, ...(gone ? { d: 'out' } : {}) });
        else out.push({ k: 'duel', a: inst, b: target, t });
      } else {
        out.push({ k: 'duel-failed', a: inst, b: target, t, ...(collected ? { d: gone ? 'self-out' : 'self' } : {}) });
      }
      continue;
    }

    if (e.eventType === 'feud-fume') {
      const pairing = e.pairing as { kind: 'feud' | 'fume'; rabbitId: string; turtleId: string } | null | undefined;
      if (!pairing) continue;
      const outs = (e.out ?? []) as string[];
      // A feud: the man BEHIND (the turtle) is the one coming. A fume: the man
      // who has had enough is named first (`actorIds[0]`) and vents on the other.
      const aggressor = pairing.kind === 'fume' ? (e.actorIds[0] ?? pairing.rabbitId) : pairing.turtleId;
      const victim = aggressor === pairing.rabbitId ? pairing.turtleId : pairing.rabbitId;
      const wreck = /\.wreck$/.test(e.momentTag);
      if (wreck) {
        const selfOut = outs.includes(aggressor) && !outs.includes(victim);
        out.push({ k: 'wrecked', a: aggressor, b: victim, t, d: selfOut ? 'self-out' : `${pairing.kind}-out` });
      } else {
        out.push({ k: 'feud-flare', a: aggressor, b: victim, t, d: pairing.kind });
      }
      continue;
    }

    if (e.eventType === 'trouble') {
      // A small crash shared — not a duel's own trouble (that is one car).
      const collected = (e.collectedIds ?? []) as string[];
      if (collected.length >= 2 && collected.length <= SHARED_WRECK_MAX_CARS && e.momentTag !== 'wreck.big-one') {
        for (let x = 0; x < collected.length; x++) {
          for (let y = x + 1; y < collected.length; y++) {
            out.push({ k: 'shared-wreck', a: collected[x], b: collected[y], t });
          }
        }
      }
      continue;
    }
  }

  // The battles, in the order each pair first met.
  for (const e of exchanges.values()) {
    if (e.n >= BATTLE_MIN_EXCHANGES) out.push({ k: 'battle', a: e.a, b: e.b, t: e.t, d: String(e.n) });
  }
  return out;
}

const cache = new WeakMap<object, RaceMemory | null>();

/**
 * The race's memory: from the tape when it is there, else the digest the
 * archive kept, else null (an archive from before D-EZ-108).
 */
export function raceMemoryOf(run: SeasonRaceResult): RaceMemory | null {
  const hit = cache.get(run);
  if (hit !== undefined) return hit;
  const fromTape = incidentsFromTape(run);
  const memory: RaceMemory | null = fromTape ? { v: 1, incidents: fromTape } : (run.memory ?? null);
  cache.set(run, memory);
  return memory;
}

/** What `archiveRace` stores on the race before the tape goes. */
export function digestRace(run: SeasonRaceResult): RaceMemory | null {
  return raceMemoryOf(run);
}
